/**
 * QueueDetailPage — 队列项详情
 * 展示队列项完整信息；重试 / 删除操作放在此处（列表行不再直接提供）。
 */
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
} from '@fluentui/react-components'
import {
  ArrowClockwiseRegular,
  DeleteRegular,
  MailRegular,
  ClockRegular,
  HistoryRegular,
  AlertUrgentRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons'
import { queueApi } from '@/api/queue'
import type { QueueItem } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'
import { formatDate } from '@/lib/format'

// ── 状态辅助（与列表页保持一致）──
type BadgeColor = 'brand' | 'warning' | 'danger' | 'success' | 'informative'

function statusBadgeColor(status: string): BadgeColor {
  switch (status) {
    case 'incoming':
      return 'brand'
    case 'active':
      return 'warning'
    case 'deferred':
      return 'warning'
    case 'bounced':
      return 'danger'
    case 'delivered':
      return 'success'
    case 'expired':
      return 'danger'
    default:
      return 'informative'
  }
}

function statusIcon(status: string, className: string): ReactNode {
  switch (status) {
    case 'incoming':
      return <MailRegular className={className} />
    case 'active':
      return <ClockRegular className={className} />
    case 'deferred':
      return <HistoryRegular className={className} />
    case 'bounced':
      return <AlertUrgentRegular className={className} />
    case 'delivered':
      return <CheckmarkCircleRegular className={className} />
    case 'expired':
      return <AlertUrgentRegular className={className} />
    default:
      return <MailRegular className={className} />
  }
}

const useStyles = makeStyles({
  mediaIcon: {
    fontSize: '22px',
    color: tokens.colorBrandForeground1,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px',
  },
  deleteName: {
    fontWeight: 600,
    color: tokens.colorStatusDangerForeground1,
  },
})

export default function QueueDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // 用未带筛选的 key 拉取完整列表，确保无论列表当前筛选如何都能定位到该项
  const { item, isLoading } = useDetailItem<QueueItem>(
    ['queue', undefined],
    () => queueApi.list(undefined),
    id as string,
    'id',
  )

  const [deleteOpen, setDeleteOpen] = useState(false)

  const retryMutation = useMutation({
    mutationFn: () => queueApi.retry(id as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue'] })
      queryClient.invalidateQueries({ queryKey: ['queue-stats'] })
      toast.success('重试成功', '队列项已加入重试')
    },
    onError: (err) =>
      toast.error('重试失败', err instanceof ApiError ? err.message : '重试失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => queueApi.delete(id as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue'] })
      queryClient.invalidateQueries({ queryKey: ['queue-stats'] })
      toast.success('删除成功', '队列项已删除')
      navigate(ROUTES.QUEUE)
    },
    onError: (err) =>
      toast.error('删除失败', err instanceof ApiError ? err.message : '删除失败'),
  })

  const stateItem = (location.state as { item?: QueueItem } | null)?.item

  return (
    <>
      <DetailLayout
        title={item?.id ?? stateItem?.id ?? '队列项'}
        subtitle="邮件队列"
        media={statusIcon((item ?? stateItem)?.status ?? '', styles.mediaIcon)}
        badge={
          <Badge appearance="filled" color={statusBadgeColor((item ?? stateItem)?.status ?? '')}>
            {(item ?? stateItem)?.status}
          </Badge>
        }
        loading={isLoading && !stateItem}
        notFound={!isLoading && !item && !stateItem}
        notFoundText="未找到该队列项"
        actions={
          <>
            <Button
              appearance="subtle"
              icon={retryMutation.isPending ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
              onClick={() => retryMutation.mutate()}
            >
              重试
            </Button>
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              onClick={() => setDeleteOpen(true)}
            >
              删除
            </Button>
          </>
        }
      >
        <DetailSection title="队列信息">
          <DetailRow label="ID" value={item?.id ?? stateItem?.id} />
          <DetailRow label="状态" value={item?.status ?? stateItem?.status} />
          <DetailRow
            label="尝试次数"
            value={`${(item ?? stateItem)?.attempts}/${(item ?? stateItem)?.max_attempts}`}
          />
          <DetailRow
            label="下次尝试"
            value={formatDate((item ?? stateItem)?.next_attempt)}
          />
          <DetailRow label="最后错误" value={(item ?? stateItem)?.last_error} full />
          <DetailRow
            label="创建时间"
            value={formatDate((item ?? stateItem)?.created_at)}
          />
        </DetailSection>
      </DetailLayout>

      {/* 删除确认 */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setDeleteOpen(false)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定要删除队列项{' '}
                <span className={styles.deleteName}>{item?.id ?? stateItem?.id}</span>{' '}
                吗？此操作不可撤销。
              </Text>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
